import { Card, CardContent } from '@/components/ui/card';
import { Clock, Wrench, TrendingUp, Moon } from 'lucide-react';
import { formatMinutesAsHours, calculateEfficiency, getEfficiencyColor } from '@/utils/payrollDateUtils';
import { PayrollSummaryRow } from '@/hooks/usePayrollSummary';

interface PayrollSummaryCardsProps {
  data: PayrollSummaryRow[];
  loading?: boolean;
  compareData?: PayrollSummaryRow[];
}

export function PayrollSummaryCards({ data, loading, compareData }: PayrollSummaryCardsProps) {
  // Calculate totals
  const totalClockMinutes = data.reduce((sum, row) => sum + row.clock_minutes, 0);
  const totalAfterHoursMinutes = data.reduce((sum, row) => sum + row.after_hours_minutes, 0);
  const techRows = data.filter(row => row.employee_type === 'technician');
  const totalProducedMinutes = techRows.reduce((sum, row) => sum + row.produced_minutes, 0);
  const techClockMinutes = techRows.reduce((sum, row) => sum + row.clock_minutes, 0);
  const overallEfficiency = calculateEfficiency(totalProducedMinutes, techClockMinutes);

  // Calculate comparison totals if available
  const compareClockMinutes = compareData?.reduce((sum, row) => sum + row.clock_minutes, 0) || 0;
  const compareTechRows = compareData?.filter(row => row.employee_type === 'technician') || [];
  const compareProducedMinutes = compareTechRows.reduce((sum, row) => sum + row.produced_minutes, 0);

  const clockDelta = compareClockMinutes > 0 
    ? ((totalClockMinutes - compareClockMinutes) / compareClockMinutes * 100).toFixed(0) 
    : null;
  const producedDelta = compareProducedMinutes > 0 
    ? ((totalProducedMinutes - compareProducedMinutes) / compareProducedMinutes * 100).toFixed(0) 
    : null;

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-16 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <Clock className="h-4 w-4" />
            Total Clock Hours
          </div>
          <div className="text-2xl font-bold">{formatMinutesAsHours(totalClockMinutes)}</div>
          {clockDelta && (
            <div className={`text-sm ${Number(clockDelta) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {Number(clockDelta) >= 0 ? '+' : ''}{clockDelta}% vs LY
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <Wrench className="h-4 w-4" />
            Total Produced Hours
          </div>
          <div className="text-2xl font-bold">{formatMinutesAsHours(totalProducedMinutes)}</div>
          {producedDelta && (
            <div className={`text-sm ${Number(producedDelta) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {Number(producedDelta) >= 0 ? '+' : ''}{producedDelta}% vs LY
            </div>
          )}
          <div className="text-xs text-muted-foreground">(Technicians only)</div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <TrendingUp className="h-4 w-4" />
            Overall Efficiency
          </div>
          <div className={`text-2xl font-bold ${getEfficiencyColor(overallEfficiency)}`}>
            {overallEfficiency !== null ? `${overallEfficiency}%` : '—'}
          </div>
          <div className="text-xs text-muted-foreground">(Produced / Clock)</div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <Moon className="h-4 w-4" />
            After-Hours Total
          </div>
          <div className="text-2xl font-bold">{formatMinutesAsHours(totalAfterHoursMinutes)}</div>
          <div className="text-xs text-muted-foreground">
            ({totalClockMinutes > 0 ? Math.round(totalAfterHoursMinutes / totalClockMinutes * 100) : 0}% of total)
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
